import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { C, ACCENT } from '../../theme';

export function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(80,120,160,0.25)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={onClose}>
      <div style={C.modal} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#1a2a3a' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#8aa0b8', fontSize:22, cursor:'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function DigitalClientsPage() {
  const { canEdit, isAdmin, isAgency } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name:'', industry:'', contact:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = () => api.getClients().then(setClients).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{ load(); },[]);

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (modal==='new') await api.createClient(form);
      else await api.updateClient(modal.id, form);
      await load(); setModal(null);
    } catch(e){ alert(e.message); } finally{ setSaving(false); }
  }

  async function handleDelete(e, id) {
    e.stopPropagation();
    if (!window.confirm('Delete this client?')) return;
    await api.deleteClient(id); load();
  }

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.industry||'').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={C.page}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28 }}>
        <h1 style={C.title}>Clients <span style={{ color:'#8aa0b8', fontWeight:400, fontSize:16 }}>({clients.length})</span></h1>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <input placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}
            style={{ padding:'8px 14px', border:'1px solid #c8d8e8', borderRadius:8, fontSize:13, outline:'none', width:200, background:'rgba(255,255,255,0.7)' }}/>
          {canEdit && isAgency && <button style={C.btnPrimary} onClick={()=>{setForm({name:'',industry:'',contact:'',notes:''});setModal('new');}}>+ New Client</button>}
        </div>
      </div>

      {loading ? <div style={C.empty}>Loading…</div> : (
        <table style={C.table}>
          <thead>
            <tr>
              <th style={C.th}>Client</th>
              <th style={C.th}>Industry</th>
              <th style={C.th}>Brands</th>
              <th style={C.th}>Plans</th>
              <th style={C.th}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length===0 ? (
              <tr><td colSpan={5} style={{...C.td,textAlign:'center',color:'#8aa0b8',padding:48}}>
                {search?'No clients match.':'No clients yet. Create your first client.'}
              </td></tr>
            ) : filtered.map(c=>{
              const brands=c.brand_count||0;
              const total=c.plan_count||0;
              return(
                <tr key={c.id} style={C.tr} onClick={()=>navigate(`/clients/${c.id}`)}
                  onMouseEnter={e=>e.currentTarget.style.background='rgba(0,144,208,0.04)'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={{...C.td,fontWeight:700}}>{c.name}</td>
                  <td style={{...C.td,color:'#6a8aaa'}}>{c.industry||'—'}</td>
                  <td style={{...C.td, color:'#6a8aaa'}}>{brands} {brands===1?'brand':'brands'}</td>
                  <td style={{...C.td, color:'#6a8aaa'}}>{total} {total===1?'plan':'plans'}</td>
                  <td style={{...C.td,textAlign:'right',whiteSpace:'nowrap'}} onClick={e=>e.stopPropagation()}>
                    {canEdit && <button style={C.btnSecondary} onClick={e=>{e.stopPropagation();setForm({name:c.name,industry:c.industry||'',contact:c.contact||'',notes:c.notes||''});setModal(c);}}>Edit</button>}
                    {isAdmin && total===0 && <button style={C.btnDanger} onClick={e=>handleDelete(e,c.id)}>Delete</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {modal && (
        <Modal title={modal==='new'?'New Client':`Edit: ${modal.name}`} onClose={()=>setModal(null)}>
          {[{key:'name',label:'Client Name *',placeholder:'e.g. Acme Retail'},{key:'industry',label:'Industry',placeholder:'e.g. Retail'},{key:'contact',label:'Contact',placeholder:'Contact person or email'}].map(f=>(
            <label key={f.key} style={{display:'block',marginBottom:14}}>
              <span style={C.label}>{f.label}</span>
              <input value={form[f.key]} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} placeholder={f.placeholder} style={C.input}/>
            </label>
          ))}
          <label style={{display:'block',marginBottom:22}}>
            <span style={C.label}>Notes</span>
            <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={3} style={{...C.input,resize:'vertical'}}/>
          </label>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button style={C.btnSecondary} onClick={()=>setModal(null)}>Cancel</button>
            <button style={C.btnPrimary} onClick={handleSave} disabled={saving}>{saving?'Saving…':modal==='new'?'Create Client':'Save'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
